import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MoonStars } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/reading";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        toast.success(data.message);
        setLoading(false);
        return;
      }
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back, seeker." : "Your journey begins.");
      navigate(redirectTo);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (key, type, label, placeholder) => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">{label}</label>
      <input
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        data-testid={`auth-${key}-input`}
        className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold/40 transition-colors duration-300"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16 relative overflow-hidden" data-testid="auth-page">
      <div
        className="absolute inset-0 opacity-30 bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1764509195128-e2f389ac3235?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHx0YXJvdCUyMGNhcmRzJTIwZGFyayUyMGVsZWdhbnR8ZW58MHx8fHwxNzg0MDE2Mjk5fDA&ixlib=rb-4.1.0&q=85)" }}
      />
      <div className="absolute inset-0 bg-black/70" />
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative glass-panel p-10 w-full max-w-md"
      >
        <div className="flex items-center gap-2 mb-8">
          <MoonStars size={28} weight="duotone" className="text-gold" />
          <h1 className="font-heading text-3xl font-semibold tracking-tighter">
            {mode === "login" ? "Return to the table" : mode === "forgot" ? "Recover your key" : "Take your seat"}
          </h1>
        </div>

        <div className="flex border border-white/15 mb-8">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              data-testid={`auth-${m}-tab`}
              className={`flex-1 py-3 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${
                mode === m ? "bg-gold text-black" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && field("name", "text", "Your name", "Luna Selene")}
          {field("email", "email", "Email", "you@example.com")}
          {mode !== "forgot" && field("password", "password", "Password", "••••••••")}
          {mode === "forgot" && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Enter your email and we'll send you a link to reset your password.
            </p>
          )}
          {error && (
            <p className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="auth-error-message">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-button"
            className="w-full py-4 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] disabled:opacity-50 transition-colors duration-300"
          >
            {loading ? "Consulting the cards..." : mode === "login" ? "Enter" : mode === "forgot" ? "Send reset link" : "Begin"}
          </button>
          {mode === "login" && (
            <button
              type="button"
              onClick={() => { setMode("forgot"); setError(""); }}
              data-testid="forgot-password-link"
              className="block mx-auto text-xs text-muted-foreground hover:text-gold transition-colors duration-300"
            >
              Forgot password?
            </button>
          )}
          {mode === "forgot" && (
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="back-to-login-link"
              className="block mx-auto text-xs text-muted-foreground hover:text-gold transition-colors duration-300"
            >
              ← Back to sign in
            </button>
          )}
        </form>
      </motion.div>
    </div>
  );
}
